import { useLayoutEffect, useRef, useState } from "react";
import { Box, Card, Stack, Typography } from "@mui/material";
import { useQueries } from "@tanstack/react-query";
import useApi from "../../hooks/useApi.js";
import UserChip from "../../components/UserChip.js";

// Hand-picked quotes from real users — update this list directly to change testimonials.
const TESTIMONIALS = [
  {
    username: "jazz",
    quote:
      "This is the closest to what I envisioned as my <b>dream tracker</b> that I've ever come across. I love how clean it all is.",
  },
  {
    username: "crocpoint",
    quote:
      "Curate has been a complete breath of fresh air for media tracking. By seamlessly combining movies, series and books into one structured platform, it finally solved my app fatigue and became my <b>single go-to hub</b>.",
  },
  {
    username: "thesquid",
    quote:
      "<b>So good!!!</b> I love the look and feel, the tracking interface is very intuitive, bugs are fixed quickly and new features are added all the time.",
  },
  {
    username: "gbrooks",
    quote:
      "As an already avid user of both Goodreads and IMDb, I have been super impressed by Curate. Having <b>everything in one place</b> is incredibly convenient and I particularly love the new updates with the explore collections and movie stats.",
  },
  {
    username: "johannes",
    quote:
      "I am absolutely blown away and thrilled by Curate! As a film student and DOP, I've been searching for a Letterboxd alternative for ages - Curate solves everything I found frustrating about it. Already <b>completely in love</b>!",
  },
  {
    username: "turtle",
    quote:
      "I love the personal feel of Curate, it's very much about you and what you choose to track. The social features are a bonus and don't distract from the main purpose of the app. <b>Highly recommend!</b>",
  },
  {
    username: "will",
    quote:
      "I think it's bloody marvellous, but then again I'm biased... I did build the thing. Sign up (<b>it's free!</b>), play around, and let me know what you think: every bit of feedback shapes what gets built next.",
  },
];

// Render this many copies of the list back-to-back so scrolling in either
// direction always has more content, then silently snap back to the
// equivalent spot in the middle copy — a seamless infinite loop.
const LOOP_COPIES = 3;
const LOOPED_TESTIMONIALS = Array.from({ length: LOOP_COPIES }, (_, copy) =>
  TESTIMONIALS.map((t) => ({ ...t, copy })),
).flat();

// Splits a quote on hand-authored <b>...</b> markers and renders the
// matched segments emphasised, so testimonials can highlight a phrase
// without pulling in a full markdown parser.
function renderEmphasizedQuote(quote) {
  return quote.split(/<b>(.*?)<\/b>/g).map((part, i) =>
    i % 2 === 1 ? (
      <Box
        key={i}
        component="span"
        sx={{ fontWeight: 700, color: "primary.main" }}
      >
        {part}
      </Box>
    ) : (
      part
    ),
  );
}

function TestimonialCard({ quote, username, avatarUrl }) {
  return (
    <Card
      elevation={0}
      sx={{
        flexShrink: 0,
        scrollSnapAlign: "start",
        bgcolor: (theme) => theme.palette.secondary.main,
        width: { xs: "100%", sm: "calc((100% - 32px) / 3)" },
        minHeight: { xs: 200, sm: 240 },
        p: 3,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          flexGrow: { xs: 1, sm: 0 },
          justifyContent: { xs: "center", sm: "flex-start" },
        }}
      >
        <Typography
          variant="body1"
          color="text.primary"
          sx={{ textWrap: "balance", textAlign: { xs: "center", sm: "left" } }}
        >
          {renderEmphasizedQuote(quote)}
        </Typography>
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          justifyContent={{ xs: "center", sm: "flex-start" }}
        >
          <UserChip username={username} avatarUrl={avatarUrl} size={28} />
        </Stack>
      </Box>
    </Card>
  );
}

// Width (in px) of one card + gap, measured from the live DOM so it stays
// correct across breakpoints without duplicating the sx width logic above.
function getStep(trackEl) {
  return trackEl.scrollWidth / LOOPED_TESTIMONIALS.length;
}

export default function TestimonialsSection() {
  const { fetchUserProfile } = useApi();
  const trackRef = useRef(null);
  const scrollEndTimer = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  // Share the cache with Profile.js (queryKey ["profile", username]) so avatars
  // are already warm if a visitor lands here after browsing profiles.
  const profileQueries = useQueries({
    queries: TESTIMONIALS.map(({ username }) => ({
      queryKey: ["profile", username],
      queryFn: () => fetchUserProfile(username),
      staleTime: 1000 * 60 * 10,
      // This is a public page — a missing/renamed testimonial account should
      // degrade to the fallback avatar, not crash the homepage via the app-wide
      // QueryErrorBoundary (which throws on any 4xx/5xx by default).
      throwOnError: false,
    })),
  });
  const avatarByUsername = Object.fromEntries(
    TESTIMONIALS.map((t, i) => [t.username, profileQueries[i]?.data?.avatar_url]),
  );

  // Start scrolled into the middle copy so both scroll directions have
  // adjacent content to loop into immediately.
  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollLeft = getStep(el) * TESTIMONIALS.length;
  }, []);

  const normalizeScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    const step = getStep(el);
    const loopWidth = step * TESTIMONIALS.length;
    let { scrollLeft } = el;

    // Wrap back into the middle copy once we've drifted into an outer one.
    if (scrollLeft < loopWidth * 0.5) scrollLeft += loopWidth;
    else if (scrollLeft >= loopWidth * 1.5) scrollLeft -= loopWidth;
    if (scrollLeft !== el.scrollLeft) el.scrollLeft = scrollLeft;

    const logicalIndex =
      ((Math.round((scrollLeft - loopWidth) / step) % TESTIMONIALS.length) +
        TESTIMONIALS.length) %
      TESTIMONIALS.length;
    setActiveIndex(logicalIndex);
  };

  const handleScroll = () => {
    clearTimeout(scrollEndTimer.current);
    scrollEndTimer.current = setTimeout(normalizeScroll, 100);
  };

  const goToIndex = (targetIndex) => {
    const el = trackRef.current;
    if (!el) return;
    const step = getStep(el);
    const loopWidth = step * TESTIMONIALS.length;
    const currentCopy = Math.floor(el.scrollLeft / loopWidth);
    el.scrollTo({
      left: step * (currentCopy * TESTIMONIALS.length + targetIndex),
      behavior: "smooth",
    });
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "lg" }}>
      <Box
        ref={trackRef}
        onScroll={handleScroll}
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 2,
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          py: 1,
        }}
      >
        {LOOPED_TESTIMONIALS.map((t) => (
          <TestimonialCard
            key={`${t.username}-${t.copy}`}
            quote={t.quote}
            username={t.username}
            avatarUrl={avatarByUsername[t.username]}
          />
        ))}
      </Box>

      <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
        {TESTIMONIALS.map((t, i) => (
          <Box
            key={t.username}
            component="button"
            onClick={() => goToIndex(i)}
            aria-label={`Show testimonial from @${t.username}`}
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              border: "none",
              p: 0,
              cursor: "pointer",
              backgroundColor: (theme) =>
                i === activeIndex
                  ? theme.palette.primary.main
                  : theme.palette.action.disabled,
            }}
          />
        ))}
      </Stack>
    </Box>
  );
}
